import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAuth } from '@restworld/feature-authentication';

import useKv from '../job-listing/hooks/use-kv';
import { useNonCompliantJobPositionsCount } from './use-non-compliant-job-positions';

// Snooze duration after the user dismisses the banner. Once it elapses the KV key
// expires and the banner reappears, provided non-compliant job positions still exist.
const DISMISS_DURATION_SECONDS = 24 * 60 * 60; // 24 hours
const DISMISS_DURATION_MS = DISMISS_DURATION_SECONDS * 1000;
const isDev = process.env.NODE_ENV === 'development';

/**
 * Drives the pay-transparency banner: gates it to PPU users with at least one
 * non-compliant job position, and handles the per-user 24h dismissal snooze via
 * the shared KV store (same pattern as the performance-onboarding banner).
 */
export function usePayTransparencyBannerState() {
  const { user } = useAuth();
  const { getKV, setKV } = useKv();

  const isUsingPPU = Boolean(user?.using_ppu_business_model);

  const { count, isLoading: isCountLoading } = useNonCompliantJobPositionsCount({ enabled: isUsingPPU });

  const [isDismissed, setIsDismissed] = useState(false);
  const [isKvLoading, setIsKvLoading] = useState(true);

  const kvKey = useMemo(() => (user?.id ? `pay-transparency-banner-dismissed-${user.id}` : null), [user?.id]);

  useEffect(() => {
    if (!isUsingPPU || !kvKey) {
      setIsKvLoading(false);
      return;
    }

    if (isDev) {
      setIsKvLoading(false);
      return;
    }

    const checkDismissed = async () => {
      const data = await getKV(kvKey);
      const dismissedAt = data?.value ? parseInt(data.value, 10) : null;

      if (dismissedAt && Date.now() - dismissedAt < DISMISS_DURATION_MS) {
        setIsDismissed(true);
      }

      setIsKvLoading(false);
    };

    checkDismissed();
  }, [isUsingPPU, kvKey, getKV]);

  const handleDismiss = useCallback(async () => {
    setIsDismissed(true);
    if (kvKey && !isDev) {
      await setKV({
        key: kvKey,
        value: Date.now().toString(),
        type: 'pay_transparency_banner_dismissed',
        expirySeconds: DISMISS_DURATION_SECONDS,
      });
    }
  }, [kvKey, setKV]);

  // Never surface the pay-transparency reminder in local development — it's noise while
  // working against seeded/prod-mirror data. Prod and Vercel previews (NODE_ENV=production)
  // are unaffected.
  const shouldShowBanner = !isDev && isUsingPPU && count > 0 && !isDismissed && !isKvLoading && !isCountLoading;

  return { shouldShowBanner, handleDismiss };
}
